import { useEffect, useState } from 'react'

import { formatWhen, t } from '../i18n'
import type { MyInvite } from '../types'

interface Props {
  /** Приглашения без ответа; показываем по одному */
  invites: MyInvite[]
  onAccept: (invite: MyInvite) => Promise<void>
  onDecline: (invite: MyInvite) => Promise<void>
  onDetails: (invite: MyInvite) => void
  onLater: (invite: MyInvite) => void
}

function initials(name: string): string {
  return name.trim().slice(0, 1).toUpperCase() || '?'
}

/** Всплывающее приглашение при входе в приложение. Вторая половина
 *  доставки: первая — сообщение бота с теми же двумя ответами. */
export default function InvitePopup({ invites, onAccept, onDecline, onDetails, onLater }: Props) {
  const [busy, setBusy] = useState(false)
  const invite = invites[0]

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  if (!invite) return null
  const game = invite.game
  const free = Math.max(game.max_players - game.joined_count, 0)
  const joined = game.slots.filter((s) => s.status === 'joined')

  async function act(fn: (i: MyInvite) => Promise<void>) {
    setBusy(true)
    try {
      await fn(invite)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sheet-backdrop center">
      <div className="popup" role="dialog" aria-modal="true" aria-label={t().invitesYou(invite.inviter_name, game.kind)}>
        <div className="row" style={{ gap: 10 }}>
          {invite.inviter_photo ? (
            <img className="avatar big" src={invite.inviter_photo} alt="" />
          ) : (
            <span className="avatar big">{initials(invite.inviter_name)}</span>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="title">{t().invitesYou(invite.inviter_name, game.kind)}</div>
            {invites.length > 1 && <div className="muted small">{t().inviteCount(1, invites.length)}</div>}
          </div>
        </div>

        <div className="popup-game">
          {game.kind !== 'game' && (
            <div className="badge gold" style={{ marginBottom: 6 }}>
              {game.kind === 'training' ? t().trainingBadge : t().tournamentBadge(game.kind)}
            </div>
          )}
          <div style={{ fontWeight: 700 }}>{formatWhen(game.starts_at)}</div>
          <div>{game.club.name}</div>
          <div className="muted small" style={{ marginTop: 4 }}>
            {t().level} {game.level_min.toFixed(2)}–{game.level_max.toFixed(2)}
            {game.price_per_player != null && ` · ${game.price_per_player} € ${t().perPlayer}`}
          </div>
          <div className="row between" style={{ marginTop: 10 }}>
            <div className="avatars">
              {joined.slice(0, 6).map((s) =>
                s.player.photo_url ? (
                  <img key={s.id} className="avatar" src={s.player.photo_url} alt="" />
                ) : (
                  <span key={s.id} className="avatar">
                    {initials(s.player.first_name)}
                  </span>
                ),
              )}
              {joined.length > 6 && <span className="avatar more">+{joined.length - 6}</span>}
            </div>
            <span className={free > 0 ? 'badge' : 'badge neutral'}>{free > 0 ? t().freeSlots(free) : t().full}</span>
          </div>
        </div>

        <div className="row" style={{ gap: 8 }}>
          <button style={{ flex: 1 }} disabled={busy} onClick={() => act(onAccept)}>
            {free > 0 ? t().inviteGo : t().inviteGoWaitlist}
          </button>
          <button className="ghost" style={{ flex: 1 }} disabled={busy} onClick={() => act(onDecline)}>
            {t().inviteNo}
          </button>
        </div>
        <div className="row" style={{ gap: 8, marginTop: 8 }}>
          <button className="link" disabled={busy} onClick={() => onDetails(invite)}>
            {t().inviteMore}
          </button>
          <button className="link" disabled={busy} onClick={() => onLater(invite)}>
            {t().inviteLater}
          </button>
        </div>
      </div>
    </div>
  )
}
